import { useCallback, useContext } from 'react';
import { useMutation } from '@tanstack/react-query';
import { useModal, QuantAnalysisContext, QuantAnalysisVersionContext, useInvalidateQuantFolders, type BaseModalProps } from '@trinity/chat-components';
import { Body } from '@trinity/chat-components/components/Modal/Body.js';
import { ButtonSet } from '@trinity/chat-components/components/Modal/ButtonSet.js';
import { Modal } from '@trinity/chat-components/components/Modal/Modal.js';
import { ButtonActivityIndicator } from '@trinity/chat-components/components/presentation/Button.ActivityIndicator.js';
import { ButtonOutlined } from '@trinity/chat-components/components/presentation/ButtonOutlined.js';
import { useChatApiClient } from '@trinity/chat-components/containers/Chat/useApiClient.js';
import { ProjectDetailsContext } from '@/screens/Project.Main/Context';
import styles from './style/Modal.DeleteAnalysis.module.css';

type Props = BaseModalProps & {
  folderId: number;
};

export const DeleteQuantFolderModal = (props: Props) => {

  const quantAnalysis = useContext(QuantAnalysisContext);
  const quantVersion = useContext(QuantAnalysisVersionContext);
  const projectDetails = useContext(ProjectDetailsContext);
  const apiClient = useChatApiClient();
  const projectId = projectDetails.query.data?.project?.id;
  const invalidateQuantFolders = useInvalidateQuantFolders();



  const mutation = useMutation({
    mutationFn: () => {
      return apiClient.quantAnalysis.deleteQuantFolder({
        quantAnalysisId: quantAnalysis.id,
        folderId: props.folderId,
        projectId: projectId,
      });
    }, onSuccess: () => {
      invalidateQuantFolders(quantAnalysis.id, projectId, quantVersion?.id);
      props.onClose();
    },
  });

  const handleDelete = useCallback(() => {
    mutation.mutateAsync();
  }, [mutation]);

  return (
    <Modal
      className={styles.root}
      disableEscapeClose={mutation.isPending}
      disableOverlayClick={mutation.isPending}
      hideCloseIcon
      open={props.open}
      onClose={props.onClose}>
      <Body className={styles.body}>
        <div className={styles.message}>
          Are you sure you want to delete this folder?
        </div>
      </Body>
      <ButtonSet className={styles.btns}>
        <ButtonOutlined
          borderWidth="thin"
          className={styles.btn}
          disabled={mutation.isPending}
          color="silver"
          onClick={props.onClose}>
          Cancel
        </ButtonOutlined>
        <ButtonActivityIndicator
          className={styles.btn}
          color="destructive"
          indicatorSize={12}
          loading={mutation.isPending}
          onClick={handleDelete}>
          Confirm
        </ButtonActivityIndicator>
      </ButtonSet>
    </Modal>
  );
};

export const useDeleteQuantFolderModal = () => useModal(DeleteQuantFolderModal);